/**
 * Hand-drawn exam icons for the "Your exam is already here" tiles. One
 * stroke weight, round caps, drawn in the licensing body's colour. Strokes
 * draw in when the tiles arrive (.hp-draw, pathLength=1); each icon has its
 * own small hover action that says what the profession does (styles in the
 * hp-xi block of index.css).
 */

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.25,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Medical lab: a sample tube whose level rises and bubbles on hover. */
function TestTube() {
  return (
    <svg viewBox="0 0 48 48" className="hp-xi hp-xi-tube h-12 w-12" aria-hidden>
      <path className="liquid" d="M19.6 24h8.8v10.6a4.4 4.4 0 0 1-8.8 0z" fill="currentColor" opacity="0.22" />
      <circle className="bubble" cx="22.6" cy="33" r="1.3" fill="currentColor" />
      <circle className="bubble" cx="25.6" cy="29" r="1" fill="currentColor" style={{ animationDelay: "0.45s" }} />
      <circle className="bubble" cx="23.8" cy="27" r="0.8" fill="currentColor" style={{ animationDelay: "0.9s" }} />
      <path className="hp-draw" pathLength={1} {...STROKE} d="M18 6.5v28.2a6 6 0 0 0 12 0V6.5" />
      <path className="hp-draw" pathLength={1} {...STROKE} d="M15.5 6.5h17" />
      <path className="hp-draw" pathLength={1} {...STROKE} strokeWidth={1.75} d="M30 14h-3.5M30 19h-3.5" opacity="0.6" />
    </svg>
  );
}

/** Dentistry: a tooth that gives a little wiggle and catches a sparkle. */
function Tooth() {
  return (
    <svg viewBox="0 0 48 48" className="hp-xi hp-xi-tooth h-12 w-12" aria-hidden>
      <g className="tooth">
        <path
          className="hp-draw"
          pathLength={1}
          {...STROKE}
          d="M16.5 9.5c-3.6 0-6.2 2.9-6.2 6.8 0 3.3 1.3 5.6 2.3 8.4 1 2.9 1.2 6.5 1.9 9.9.5 2.5 1.6 4.9 3.5 4.9 2.1 0 2.7-2.5 3.3-5.2.6-2.5 1.2-4.7 2.7-4.7s2.1 2.2 2.7 4.7c.6 2.7 1.2 5.2 3.3 5.2 1.9 0 3-2.4 3.5-4.9.7-3.4.9-7 1.9-9.9 1-2.8 2.3-5.1 2.3-8.4 0-3.9-2.6-6.8-6.2-6.8-3.1 0-4.6 1.8-7.5 1.8s-4.4-1.8-7.5-1.8z"
        />
        <path className="hp-draw" pathLength={1} {...STROKE} strokeWidth={1.75} d="M15.4 16.2c.4-1.7 1.6-2.8 3.3-3" opacity="0.55" />
      </g>
      <path className="spark" d="M38 3.5l1.1 3 3 1.1-3 1.1-1.1 3-1.1-3-3-1.1 3-1.1z" fill="currentColor" />
    </svg>
  );
}

/** Lab technician: a microscope that leans in to look, its light pulsing. */
function Microscope() {
  return (
    <svg viewBox="0 0 48 48" className="hp-xi hp-xi-scope h-12 w-12" aria-hidden>
      <circle className="light" cx="21.5" cy="35.5" r="2.2" fill="currentColor" opacity="0.35" />
      <g className="tube">
        <path className="hp-draw" pathLength={1} {...STROKE} d="M17.8 7.4l5.6-2.4 7.2 15.6-5.6 2.4z" />
        <path className="hp-draw" pathLength={1} {...STROKE} d="M26.4 23.4l1.5 3.2" />
      </g>
      <path className="hp-draw" pathLength={1} {...STROKE} d="M28.6 18.6c4.6 1.4 7.7 5.4 7.7 10.2 0 5-3.4 9.6-8.3 11.9" />
      <path className="hp-draw" pathLength={1} {...STROKE} d="M13.5 31h21.8" />
      <path className="hp-draw" pathLength={1} {...STROKE} d="M11 41h26" />
    </svg>
  );
}

/** Hematology: a blood drop with a cell inside that pulses on hover. */
function BloodDrop() {
  return (
    <svg viewBox="0 0 48 48" className="hp-xi hp-xi-drop h-12 w-12" aria-hidden>
      <g className="drop">
        <path className="hp-draw" pathLength={1} {...STROKE} d="M24 6.5c5.6 7.4 11.5 14.3 11.5 21.3a11.5 11.5 0 0 1-23 0C12.5 20.8 18.4 13.9 24 6.5z" />
        <circle className="cell" cx="24" cy="29" r="4.6" fill="currentColor" opacity="0.25" />
        <circle className="hp-draw" pathLength={1} {...STROKE} strokeWidth={1.75} cx="24" cy="29" r="4.6" />
        <path className="hp-draw" pathLength={1} {...STROKE} strokeWidth={1.75} d="M17.6 24.5c.6-2 1.6-3.8 2.8-5.4" opacity="0.55" />
      </g>
    </svg>
  );
}

/** Your own notes: lines rewrite themselves while the spark turns. */
function NotesSpark() {
  return (
    <svg viewBox="0 0 48 48" className="hp-xi hp-xi-notes h-12 w-12" aria-hidden>
      <rect className="hp-draw" pathLength={1} {...STROKE} x="8.5" y="7.5" width="22" height="30" rx="4.5" />
      <path className="hp-draw line" pathLength={1} {...STROKE} strokeWidth={2} d="M14 16h11" />
      <path className="hp-draw line" pathLength={1} {...STROKE} strokeWidth={2} d="M14 21.5h11" style={{ animationDelay: "0.12s" }} />
      <path className="hp-draw line" pathLength={1} {...STROKE} strokeWidth={2} d="M14 27h6" style={{ animationDelay: "0.24s" }} />
      <path className="spark-big" d="M35 25l1.7 4.8 4.8 1.7-4.8 1.7L35 38l-1.7-4.8-4.8-1.7 4.8-1.7z" fill="currentColor" />
      <path className="spark-small" d="M41 13l.9 2.5 2.5.9-2.5.9-.9 2.5-.9-2.5-2.5-.9 2.5-.9z" fill="currentColor" />
    </svg>
  );
}

/** Picks the icon from the exam's URL slug (or name), which is the same in every language. */
export function ExamIcon({ href }: { href: string }) {
  if (/dent/i.test(href)) return <Tooth />;
  if (/hemat/i.test(href)) return <BloodDrop />;
  if (/technician/i.test(href) && !/medical-laboratory-technologist/i.test(href)) return <Microscope />;
  return <TestTube />;
}

export { NotesSpark };
